import type {
  MerchantSummary,
  MonthlyRow,
  MerchantStatus,
  MonthStatus,
} from "./types";
import { MERCHANT_DEFS } from "./merchants";
import {
  MCC_CB_PROFILE,
  MCC_DRIFT,
  MCC_VISA,
  MCC_ATV,
  BASE,
  NOW_YYYYMM,
} from "./seasonality";

export function seeded(s: number): () => number {
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

export function addM(base: string, n: number): string {
  const d = new Date(base);
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 7);
}

export function monthsLive(goLive: string): number {
  const [gy, gm] = goLive.split("-").map(Number);
  const [ny, nm] = NOW_YYYYMM.split("-").map(Number);
  return Math.max(0, (ny - gy) * 12 + (nm - gm) + 1);
}

export const MOS: string[] = Array.from({ length: 12 }, (_, i) => addM(BASE, i));

export interface SimulationOutput {
  merchantSummaries: MerchantSummary[];
  allMonthly: MonthlyRow[];
}

/**
 * Build 30-merchant, 12-month portfolio simulation.
 * Ported from the archived TSX prototype; pure function, deterministic per seed.
 */
export function generateAllData(): SimulationOutput {
  const ms: MerchantSummary[] = [];
  const am: MonthlyRow[] = [];

  MERCHANT_DEFS.forEach((m, mi) => {
    const rng = seeded(mi * 997 + 12345);
    let cp = 0;
    let cr = 0;
    let ct4 = 0;
    let ct15 = 0;
    let cv = 0;
    let term = false;
    let tDate: string | null = null;
    let pkV = 0;
    let warn = false;

    const rows: MonthlyRow[] = [];
    const drift = MCC_DRIFT[m.mcc] || 0.00003;

    for (let mo = 0; mo < 12; mo++) {
      const rel = mo - m.onboardMo;

      if (mo < m.onboardMo || term) {
        rows.push({
          month: MOS[mo],
          relMonth: rel,
          purchases: 0,
          refundCount: 0,
          tc40: 0,
          tc15: 0,
          vampRatio: 0,
          volume: 0,
          status: "Pre-onboard",
          active: false,
          merchant_id: m.id,
          merchant_name: m.name,
          mcc: m.mcc,
        });
        continue;
      }

      const prof = MCC_CB_PROFILE[m.mcc] || new Array(12).fill(0.6);
      const profileIdx = Math.min(Math.max(rel, 0), 11);
      const driftAccum = m.cbMult > 1 ? drift * rel * (m.cbMult - 1) * 8 : 0;
      const rawCbRate = (m.baseCbRate + driftAccum) * m.cbMult * prof[profileIdx];

      const purch = Math.round(m.subs * (0.94 + rng() * 0.12));
      const refCnt = Math.round(purch * m.refundRate * (0.85 + rng() * 0.3));
      const t4 = Math.round(purch * rawCbRate * 0.6 * (0.75 + rng() * 0.5));
      const t15 = Math.round(purch * rawCbRate * 0.4 * (0.75 + rng() * 0.5));
      const vol = purch * m.price;
      const vamp = purch > 0 ? ((t4 + t15) / purch) * 100 : 0;

      cp += purch;
      cr += refCnt;
      ct4 += t4;
      ct15 += t15;
      cv += vol;
      if (vamp > pkV) pkV = vamp;

      let st: MonthStatus = "Active";
      if (vamp >= 1.2) {
        st = "Warning";
        warn = true;
      }
      if (m.terminateMo !== null && rel >= m.terminateMo) {
        st = "Terminated";
        term = true;
        tDate = MOS[mo];
      }

      rows.push({
        month: MOS[mo],
        relMonth: rel,
        purchases: purch,
        refundCount: refCnt,
        tc40: t4,
        tc15: t15,
        vampRatio: Math.round(vamp * 1000) / 1000,
        volume: Math.round(vol),
        status: st,
        active: true,
        merchant_id: m.id,
        merchant_name: m.name,
        mcc: m.mcc,
      });
    }

    const overallStatus: MerchantStatus = m.terminateMo !== null
      ? "Terminated"
      : warn
        ? "Warning"
        : "Active";
    const ov = cp > 0 ? ((ct4 + ct15) / cp) * 100 : 0;

    ms.push({
      ...m,
      status: overallStatus,
      terminationDate: tDate,
      totalPurchases: cp,
      totalRefunds: cr,
      totalTc40: ct4,
      totalTc15: ct15,
      totalVolume: Math.round(cv),
      refundRatePct: cp > 0 ? Math.round((cr / cp) * 1000) / 10 : 0,
      fraudRatioPct: Math.round(ov * 1000) / 1000,
      tc40RatioPct: cp > 0 ? Math.round((ct4 / cp) * 10000) / 100 : 0,
      tc15RatioPct: cp > 0 ? Math.round((ct15 / cp) * 10000) / 100 : 0,
      peakVampRatio: Math.round(pkV * 1000) / 1000,
      visaPct: MCC_VISA[m.mcc] || 0.62,
      atv: MCC_ATV[m.mcc] || "—",
      monthsLive: monthsLive(m.goLive),
    });

    am.push(...rows);
  });

  return { merchantSummaries: ms, allMonthly: am };
}

/** Given a merchant snapshot + MCC ramp, project months until 1.5% Visa breach. */
export function monthsToBreach(
  currentVampPct: number,
  mccRampBpsPerMo: number,
  threshold = 1.5
): number {
  if (mccRampBpsPerMo <= 0) return 999;
  if (currentVampPct >= threshold) return 0;
  const gap = threshold - currentVampPct;
  return Math.max(0, Math.round(gap / mccRampBpsPerMo));
}
